import type { Event, EventVideoConference, EventWebinar, NodeOrganization } from 'models';
import { useMemo } from 'react';
import { usePermissions } from 'permissions';
import { Form, FormActions, FormLayout, Modal, message } from 'ui';
import {
  AttachmentsFormContent,
  type AttachmentsFormContentValues,
  ChannelFormContent,
  DescriptionImageFormContent,
  DetailFormContent,
  DurationFormContent,
  type ExtendedChannelFormContentValues,
  type ExtendedDescriptionImageFormContentValues,
  type ExtendedDetailFormContentValues,
  type ExtendedDurationFormValues,
  type ExtendedInvitationsDuplicateFormValues,
  InvitationsDuplicateFormContent,
} from '../../components';
import { useUploadEventAttachments, useUploadEventCoverPhoto } from '../../hooks';
import { useDuplicateEvent } from '../../queries';
import { useCreateFormRequestData, useGetInitialValues } from './hooks';

export type DuplicateEventFormValues = AttachmentsFormContentValues &
  ExtendedChannelFormContentValues &
  ExtendedDescriptionImageFormContentValues &
  ExtendedDetailFormContentValues &
  ExtendedDurationFormValues &
  ExtendedInvitationsDuplicateFormValues;

type P = {
  event: EventVideoConference | EventWebinar;
  onCancel: () => void;
  onSuccess: (eventData: Event) => void;
  organization?: NodeOrganization;
};

const DuplicateEventForm = ({ event, onCancel, onSuccess, organization }: P) => {
  const { isPending, mutate: duplicateEvent } = useDuplicateEvent();
  const { canManageEventChannels } = usePermissions();
  const { isUploadingAttachments, uploadEventAttachments } = useUploadEventAttachments();
  const { isUploadingCoverPhoto, uploadEventCoverPhoto } = useUploadEventCoverPhoto();
  const initialValues = useGetInitialValues({ event, organization });
  const createFormRequestData = useCreateFormRequestData();

  const onSubmit = async (formValues: DuplicateEventFormValues) => {
    await uploadEventCoverPhoto({ eventId: event.id, heroImage: formValues.heroImage });
    await uploadEventAttachments({ attachments: formValues.attachments, eventId: event.id });
    duplicateEvent(createFormRequestData(formValues, event), {
      onSuccess(eventData) {
        message.success('Event was duplicated.');
        onSuccess(eventData);
      },
    });
  };

  const isLoading = useMemo(
    () => isPending || isUploadingAttachments || isUploadingCoverPhoto,
    [isPending, isUploadingAttachments, isUploadingCoverPhoto],
  );

  return (
    <Form<DuplicateEventFormValues> initialValues={initialValues} onFinish={onSubmit}>
      <FormLayout>
        <DetailFormContent<DuplicateEventFormValues> mode="duplicate" />
        <DurationFormContent<DuplicateEventFormValues> />
        <InvitationsDuplicateFormContent<DuplicateEventFormValues>
          label="Do you want to duplicate event with all invitees?"
          noDescription="There will be no attendees for this event, once this event is duplicated"
          title="Invitations"
          yesDescription="All current invitees will get invitations for this event"
        />
        <DescriptionImageFormContent<DuplicateEventFormValues> />
        {canManageEventChannels({}) ? <ChannelFormContent<DuplicateEventFormValues> /> : null}
        <AttachmentsFormContent<DuplicateEventFormValues> />
      </FormLayout>
      <Modal.HugContent.Footer>
        <FormActions
          right={{
            onClick: onCancel,
          }}
          submit={{
            children: 'Duplicate',
            loading: isLoading,
          }}
        />
      </Modal.HugContent.Footer>
    </Form>
  );
};

export default DuplicateEventForm;
